// Fronteira de erro em torno da tela ativa.
//
// Existe por uma medição, não por precaução: em produção, a Visão geral lançou
// `Cannot read properties of undefined` em render e **o back office inteiro
// virou uma página em branco** — sem barra lateral, sem navegação e sem o
// botão "Sair". O React desmonta a árvore toda quando ninguém captura.
//
// O defeito que causou aquilo foi corrigido onde nasceu. Esta fronteira é a
// outra metade: o próximo defeito em render de uma tela custa **aquela tela**,
// e não a sessão inteira de quem está operando. Fica em volta do conteúdo, e
// não em volta da casca, exatamente para que a casca sobreviva.
//
// Ela **não** tenta esconder o erro. Mostra o que aconteceu, porque a pessoa
// que opera precisa poder relatar — e oferece o caminho de volta.

import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode; /** Muda quando se troca de tela: rearma. */ chave: string };
type Estado = { erro: Error | null };

export class Fronteira extends Component<Props, Estado> {
  override state: Estado = { erro: null };

  static getDerivedStateFromError(erro: Error): Estado {
    return { erro };
  }

  override componentDidUpdate(anterior: Props): void {
    // Trocar de tela rearma a fronteira. Sem isto, uma tela que quebrou uma vez
    // deixaria o erro na frente das outras — a navegação pararia de funcionar
    // por um defeito que não é dela.
    if (anterior.chave !== this.props.chave && this.state.erro !== null) {
      this.setState({ erro: null });
    }
  }

  override componentDidCatch(erro: Error, info: ErrorInfo): void {
    // O console é onde isto foi encontrado da primeira vez, e é o que um
    // relato de quem opera consegue copiar.
    // eslint-disable-next-line no-console
    console.error("Tela quebrou:", erro, info.componentStack);
  }

  override render(): ReactNode {
    const { erro } = this.state;
    if (erro === null) return this.props.children;
    return (
      <div className="vazio" role="alert">
        <h2 style={{ margin: "0 0 8px" }}>Esta tela quebrou.</h2>
        <p style={{ maxWidth: "46em" }}>
          O resto do back office continua de pé — escolha outra tela na lateral. Se acontecer de
          novo, o detalhe abaixo é o que ajuda a achar a causa.
        </p>
        <pre className="mono" style={{ whiteSpace: "pre-wrap", textAlign: "left", marginTop: 12 }}>
          {erro.message}
        </pre>
        <button className="btn" onClick={() => this.setState({ erro: null })}>
          Tentar de novo
        </button>
      </div>
    );
  }
}
